import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios';
import {Logger} from "../utils";
import {AccountStore} from "../stores/account/accountStore";
// `jwt-decode` can export differently depending on install/build (CJS vs ESM).
// Implement a robust decoder that tries common module shapes and falls back
// to a manual base64url decode of the payload (no signature verification).
let _jwt_mod: any;
try {
	// require may throw if module missing — catch to provide clearer error later
	_jwt_mod = require('jwt-decode');
} catch (e) {
	_jwt_mod = null;
}

const tryDecode = (token: string) => {
	if (!_jwt_mod) return null;
	if (typeof _jwt_mod === 'function') return _jwt_mod(token);
	if (_jwt_mod && typeof _jwt_mod.default === 'function') return _jwt_mod.default(token);
	if (_jwt_mod && typeof _jwt_mod.decode === 'function') return _jwt_mod.decode(token);
	if (_jwt_mod && _jwt_mod.default && typeof _jwt_mod.default.decode === 'function') return _jwt_mod.default.decode(token);
	return null;
};

// manual base64url decode fallback (no signature verification)
const manualDecode = (token: string) => {
	try {
		const parts = token.split('.');
		if (parts.length < 2) return null;
		const payload = parts[1];
		const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
		// pad with '='
		const pad = base64.length % 4;
		const padded = base64 + (pad ? '='.repeat(4 - pad) : '');
		// atob is available in browser; in case of server-side usage, use Buffer
		let json = '';
		if (typeof window !== 'undefined' && typeof window.atob === 'function') {
			json = decodeURIComponent(
				Array.prototype.map
					.call(window.atob(padded), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
					.join('')
			);
		} else {
			// Node fallback (shouldn't be used in client bundle, but safe guard)
			json = Buffer.from(padded, 'base64').toString('utf8');
		}
		return JSON.parse(json);
	} catch (e) {
		return null;
	}
};

const jwt_decode = (token: string) => {
	const res = tryDecode(token);
	if (res) return res;
	const manual = manualDecode(token);
	if (manual) return manual;
	throw new Error('jwt-decode: unexpected module shape and manual decode failed');
};

export const REQUEST_TIMEOUT = 30000;
export const DEV = process.env.NODE_ENV === 'development';
const base_production = 'https://homeclean4u.net';
const api_v1 = 'api-filo/api/v1';

const logger = Logger;

export interface AxiosRequestModel {
	config?: AxiosRequestConfig;
	options?: any;
	mustRefreshToken?: boolean;
	skipLogData?: boolean;
	untrackedErrorCodes?: Array<number>;
}

interface Clean4uCfg extends AxiosRequestConfig {
	startTime: number;
}

const getResponseSuccessHandler =
	(config: { config?: AxiosRequestConfig<any>; method?: any; url?: any; }) => {
		return (response: AxiosResponse) => {
			if (DEV) {
				const duration = Date.now() - (response.config as Clean4uCfg)?.startTime || 0;
				logger(
					`${response.config?.method} ${response.config?.url} ${
						response.config?.params ? JSON.stringify(response.config?.params) : ''
					} in ${duration}ms\n\n`,
					response,
					null,
					'success'
				);
			}
			return response;
		};
	};

export const axiosRequest = <T>(requestProps: AxiosRequestModel): Promise<AxiosResponse<T>> => {
	const {
		config = {},
		options = {},
	} = requestProps;
	const instance = axios.create({
		timeout: REQUEST_TIMEOUT,
		...options,
	});

	// Ensure the created instance includes global defaults (eg. Authorization header)
	try {
		const globalCommon = (axios.defaults.headers as any)?.common || {};
		instance.defaults.headers = instance.defaults.headers || {};
		instance.defaults.headers.common = {
			...(instance.defaults.headers.common || {}),
			...globalCommon,
		};
	} catch (e) {
		// ignore
	}

	if (DEV) {
		instance.interceptors.request.use(
			(request) => {
				(request as Clean4uCfg).startTime = Date.now();
				return request;
			},
			(error) => {
				logger('api.request error', error, null , 'error');
				Promise.reject(error);
			}
		);
	}

	instance.interceptors.response.use(
		getResponseSuccessHandler({ config }),
	);

	return instance.request(config);
};

export const request = (method: 'get' | 'post' | 'put' | 'patch' | 'delete'| 'options', url: string, data: any = null, options = {}, mustRefreshToken = true, skipLogData = false, untrackedErrorCodes = [], baseURL?: string) => {
	// Ensure baseURL follows the current environment so dev proxy works locally
	setBaseUrl(DEV);
	// (logging removed) final request details are available when needed during debugging
	return axiosRequest({
		config: {
			method,
			url,
			data,
			baseURL,
		},
		options,
		mustRefreshToken,
		skipLogData,
		untrackedErrorCodes,
	});
};

export const RequestError = (msn?: string, error?: AxiosError | any) => {
	const message = `${msn} :: ${error?.message ?? String(error)}`;
	throw new Error(message);
}

export class ServiceError extends Error {
	status?: number;
	data?: any;
	isNetworkError: boolean;
	constructor(source: string, axiosError: any) {
		super(`${source}: ${axiosError?.message}`);
		this.status = axiosError?.response?.status || 0;
		this.data = axiosError?.response?.data;
		this.isNetworkError =
			!!axiosError?.message?.includes('Network Error') ||
			!!axiosError?.message?.includes(`timeout of ${REQUEST_TIMEOUT}ms exceeded`);
		Object.setPrototypeOf(this, ServiceError.prototype);
	}
}

export const setToken = async (accessToken: string) => {
	let auth_user = jwt_decode(accessToken);
	const auth_user_store = await localStorage.getItem('auth_user');
	if (!auth_user_store) {
		await localStorage.setItem('auth_user', JSON.stringify(auth_user));
	}
	await AccountStore().setProfile(auth_user);
	return axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};

// export const setBaseUrl = (stagingMode: boolean) => {
// 	axios.defaults.baseURL = stagingMode
// 		? `${base_staging}/${api_v1}`
// 		: `${base_production}/${api_v1}`;
// };

export const setBaseUrl = (stagingMode: boolean) => {
	// if (DEV) {
	// 	// In development, use the local proxy path so requests are forwarded
	// 	// by the Create React App dev server (setupProxy.js).
	// 	axios.defaults.baseURL = `/${api_v1}`;
	// 	return;
	// }

	axios.defaults.baseURL = `${base_production}/${api_v1}`;
};
